import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Textarea,
  Checkbox,
} from 'ui'
import { useNavigate } from 'react-router-dom'
import CreatableSelect from 'react-select/creatable'
import { useDebounce } from 'utils'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useKeywordSearch } from '../../../queries/useKeywordSearch'
import { useAdminProfileById } from '../../../queries/useAdminProfileById'
import { useUpdateProfileMutation } from '../../../queries/useUpdateProfileMutation'
import { useSuggestKeywordsMutation } from '../../../queries/useSuggestKeywordsMutation'
import { useCreateKeywordMutation } from '../../../queries/useCreateKeywordMutation'
import clsx from 'clsx'
import { useSkipOnboarding } from '../../../hooks'
import { useQueryClient } from '@tanstack/react-query'

const schema = yup.object({
  contentDescription: yup
    .string()
    .required('Cannot be blank')
    .min(75, 'Must be at least 75 characters'),
  keywords: yup.array().of(yup.string().required()).optional(),
  connectionPreference: yup
    .array()
    .of(yup.string().required())
    .min(1, 'Please select at least one connection preference'),
})
export type ProfessionalProfilePart2 = yup.InferType<typeof schema>

interface ProfessionalStepTwoProps {
  onSuccess?: () => void
  hideNavigation?: boolean
  editMode?: boolean
  profileId?: string // Added profileId parameter
  targetField?: string
}

export const ProfessionalStepTwoForm: React.FC<ProfessionalStepTwoProps> = ({
  onSuccess,
  hideNavigation = false,
  editMode = false,
  profileId,
  targetField,
}) => {
  // Use admin profile query if profileId is provided, otherwise use current user
  const { data: user, isLoading: isCurrentUserLoading } = useCurrentUser()
  const {
    data: adminProfile,
    isLoading: isAdminProfileLoading,
  } = useAdminProfileById(profileId)

  // Determine which profile data to use
  const profileData = profileId ? adminProfile : user
  const isLoading = profileId ? isAdminProfileLoading : isCurrentUserLoading
  const userId = profileId || (user?.userId as string)

  const navigate = useNavigate()
  const skipOnboarding = useSkipOnboarding()

  const form = useForm<ProfessionalProfilePart2>({
    defaultValues: {
      contentDescription: '',
      keywords: [],
      connectionPreference: [],
    } as ProfessionalProfilePart2,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })
  const contentDescription = form.watch('contentDescription')
  const needsContentDescription = (contentDescription?.length ?? 0) < 75
  const hasDescriptions = !needsContentDescription

  let prompt = `Please enter ${
    75 - (contentDescription?.length ?? 0)
  } or more characters about what you do.`

  // Manage suggested keywords
  useEffect(() => {
    if (profileData) {
      form.setValue(
        'contentDescription',
        ((profileData as any)?.contentDescription as string) || ''
      )
      form.setValue(
        'keywords',
        ((profileData as any)?.keywords as string[]) || []
      )
      form.setValue(
        'connectionPreference',
        ((profileData as any)?.connectionPreference as string[]) || []
      )
    }
  }, [profileData])

  const [suggestedKeywords, setSuggestedKeywords] = useState<
    { id: number; keyword: string }[]
  >([])
  const {
    mutateAsync: suggestKeywords,
    isPending: loadingKeywords,
  } = useSuggestKeywordsMutation()

  // Track the current input value for search
  const [keywordInput, setKeywordInput] = useState('')
  const debouncedKeywordInput = useDebounce(keywordInput, 300)

  // Search for keywords based on debounced input
  const { data: searchedKeywords } = useKeywordSearch(
    debouncedKeywordInput,
    debouncedKeywordInput.length >= 2
  )

  const handleGenerateKeywords = async () => {
    if (contentDescription && contentDescription.length >= 75) {
      try {
        const keywords = await suggestKeywords({
          userId,
          description: contentDescription,
        })
        setSuggestedKeywords(keywords)
        form.setValue(
          'keywords',
          keywords?.map((k) => k.keyword).filter((_, i) => i < 4) as string[]
        )
      } catch (error) {
        console.error('Failed to generate keywords:', error)
      }
    }
  }
  const { mutateAsync: createKeyword } = useCreateKeywordMutation()
  const [userAddedKeywords, setUserAddedKeywords] = useState([] as string[])
  const addKeyword = async (keyword: string) => {
    setUserAddedKeywords([...(userAddedKeywords ?? []), keyword])
    await createKeyword({ keyword })
  }
  const userKeywords = (profileData as any)?.keywords ?? []
  const currentKeywords = form.watch('keywords') ?? []
  const usableKeywords = Array.from(
    new Set([
      ...(suggestedKeywords?.map((k) => k.keyword) ?? []),
      ...(searchedKeywords?.map((k) => k.keyword) ?? []),
      ...userKeywords,
      ...userAddedKeywords,
      ...currentKeywords,
    ])
  )

  const { mutateAsync: updateProfile } = useUpdateProfileMutation()
  const queryClient = useQueryClient()

  const handleSubmitForm = (redirect?: string) => async (
    values: ProfessionalProfilePart2
  ) => {
    await updateProfile({
      userId: userId,
      contentDescription: values.contentDescription as string,
      keywords: values.keywords as string[],
      connectionPreference: values.connectionPreference as string[],
    })
    queryClient.invalidateQueries({ queryKey: ['currentUser'] })

    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess()
    }

    // Only navigate if redirect is provided and we're not in edit mode
    if (redirect && !editMode) {
      navigate(redirect)
    }
  }

  const onSaveDraft = async () => {
    // Try to save current form data as best-effort, but always skip regardless of validation
    const values = form.getValues()
    try {
      await handleSubmitForm(undefined)(values)
    } catch {
      // Ignore errors - we're skipping anyway
    }
    skipOnboarding()
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          handleSubmitForm(editMode ? undefined : '/onboard/professional/three')
        )}
        className={clsx(isLoading && 'opacity-50')}
      >
        <div className="flex flex-col">
          <FormLabel>
            Let's Determine Your Industry And Areas Of Expertise
          </FormLabel>
          <p
            className={`font-body text-sm mt-3 ${
              targetField === 'contentDescription'
                ? 'bg-yellow-100 animate-pulse p-3 rounded-md border-l-4 border-yellow-400'
                : ''
            }`}
          >
            Tell us about a typical day at your job. What kind of projects do
            you work on? We'll use this information to recommend the best
            keywords for your profile.{' '}
            <span className="font-bold">
              (You can always change these or add more.)
            </span>
          </p>
          <p className="font-body text-sm text-body/70 my-3">
            EX. My typical day involves working with patients and other
            healthcare professionals (doctors, nurses, PA's, medical assistants)
            to help treat patients at our hospital. I spend most of my day
            working with the MRI and CT machines, but I enjoy interacting with
            patients the most!
          </p>
          <FormField
            control={form.control}
            name="contentDescription"
            render={({ field }) => (
              <FormItem className="w-full col-span-3 relative">
                <FormLabel className="hidden">Areas of Expertise</FormLabel>
                <FormControl>
                  <>
                    <Textarea {...field} />
                    {(field.value?.length || 0) < 75 && (
                      <div className="mt-1 text-xs text-body/70">
                        {75 - (field.value?.length || 0)} more characters
                        required
                      </div>
                    )}
                  </>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>Suggested Keywords</FormLabel>
          <p
            className={`font-body text-sm mt-1 ${
              targetField === 'keywords'
                ? 'bg-yellow-100 animate-pulse p-3 rounded-md border-l-4 border-yellow-400'
                : ''
            }`}
          >
            EPC will add keywords to your profile based on what you've already
            indicated. Real-world keywords help us link your profile to things
            that professionals do every day and will expose you to teachers with
            similar interests for their classroom.{' '}
            <span className="font-bold">
              You can always uncheck keywords that don't apply and add others.
            </span>
          </p>
          <p className="font-body text-sm text-body/70 mt-1">
            Keyword examples: Collaboration, team-work, critical thinking, math,
            science, technology, technical writing, etc.
          </p>
          <p className="font-body text-sm mt-3 text-body/70">
            Note: For matching to work effectively,{' '}
            <span className="font-bold">please choose 4-6 keywords.</span>
          </p>

          <div className="mt-4 mb-2">
            <Button
              type="button"
              variant="default"
              onClick={handleGenerateKeywords}
              disabled={!hasDescriptions || loadingKeywords}
              className="text-sm"
            >
              {loadingKeywords ? (
                <>
                  <Indicators.Spinner size="sm" className="mr-2" />
                  Generating Keywords...
                </>
              ) : (
                <>
                  {suggestedKeywords.length > 0 || userKeywords.length > 0
                    ? 'Regenerate Keywords'
                    : 'Generate Keywords'}
                </>
              )}
            </Button>
            {!hasDescriptions && (
              <p className="text-xs text-body/70 mt-1">
                Complete the description above to generate keywords.
              </p>
            )}
          </div>

          <div className="relative py-3">
            <FormField
              control={form.control}
              name="keywords"
              render={({ field }) => (
                <FormItem className="relative">
                  <FormLabel className="hidden">Keywords</FormLabel>
                  <FormControl>
                    <CreatableSelect
                      isDisabled={form.formState.isSubmitting}
                      isMulti
                      isClearable={false}
                      placeholder="Select or create keywords"
                      options={usableKeywords.map((keyword) => ({
                        value: keyword,
                        label: keyword,
                      }))}
                      onInputChange={(inputValue) => {
                        setKeywordInput(inputValue)
                      }}
                      value={
                        usableKeywords
                          ?.filter((keyword) => field.value?.includes(keyword))
                          .map((keyword) => ({
                            value: keyword,
                            label: keyword,
                          })) || []
                      }
                      onChange={(selectedOptions) => {
                        field.onChange(
                          selectedOptions.map((option) => option.value)
                        )
                      }}
                      onCreateOption={(inputValue) => {
                        console.log('onCreateOption', inputValue)
                        field.onChange([...(field.value || []), inputValue])
                        addKeyword(inputValue)
                      }}
                      classNames={{
                        control: () =>
                          'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-xs font-body text-body/70 md:mr-28 mt-1">
              When you start typing, we'll suggest keywords that are already in
              our system. Selecting existing keywords will help with accuracy,
              and, you won't have to type as much nor worry about spelling
              errors!
            </p>
            {usableKeywords.length === 0 && !loadingKeywords ? (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex align-center justify-center text-center font-body">
                <p className="m-auto p-3 border border-contentareabackgroundalt bg-white/80 rounded text-sm">
                  {hasDescriptions
                    ? "Click 'Generate Keywords' above to get suggested keywords based on your description."
                    : `Please complete the description above, then click 'Generate Keywords'.`}
                </p>
              </div>
            ) : null}
            {loadingKeywords ? <Indicators.AbsoluteOverlay /> : null}
          </div>
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>Connection Preferences</FormLabel>
          <p
            className={
              targetField === 'connectionPreference'
                ? 'bg-yellow-100 animate-pulse p-3 rounded-md border-l-4 border-yellow-400 font-body text-sm mt-1'
                : 'font-body text-sm mt-1'
            }
          >
            Select the ways you'd like to engage with educators and students.
            Choose all that apply to help us match you with the right
            opportunities.
          </p>

          <FormField
            control={form.control}
            name="connectionPreference"
            render={({ field }: { field: any }) => (
              <FormItem className="mt-4">
                <FormControl>
                  <div className="space-y-3">
                    {[
                      {
                        label: 'Share my career journey and answer questions',
                        value: 'careerJourney',
                      },
                      {
                        label:
                          'Provide content/subject consultation with a class',
                        value: 'classConsultation',
                      },
                      {
                        label:
                          'Support students with a classroom project by asking questions and linking current industry standards to the project',
                        value: 'supportClassProjpect',
                      },
                      {
                        label:
                          'Consult with a teacher on designing a real-world classroom project',
                        value: 'designClassProject',
                      },
                      {
                        label: 'Become of member of a CTE Advisory Committee',
                        value: 'becomeCTEAdvisor',
                      },
                      {
                        label: 'Participate in an exhibit or school fair',
                        value: 'participateInFair',
                      },
                      {
                        label:
                          'Participate as a judge at an event (e.g. science fair)',
                        value: 'judgeAnEvent',
                      },
                    ].map((option) => (
                      <div
                        key={option.value}
                        className="flex items-start space-x-3 mt-2"
                      >
                        <Checkbox
                          checked={field.value?.includes(option.value) || false}
                          onCheckedChange={(checked: boolean) => {
                            const newValue = checked
                              ? [...(field.value || []), option.value]
                              : (field.value || []).filter(
                                  (v: string) => v !== option.value
                                )
                            field.onChange(newValue)
                          }}
                        />
                        <label
                          className="text-sm leading-6 cursor-pointer font-body"
                          onClick={() => {
                            const isChecked =
                              field.value?.includes(option.value) || false
                            const newValue = !isChecked
                              ? [...(field.value || []), option.value]
                              : (field.value || []).filter(
                                  (v: string) => v !== option.value
                                )
                            field.onChange(newValue)
                          }}
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {!hideNavigation ? (
          <div className="flex-col flex mt-12 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="mx-auto uppercase"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>Next: Review Your Bio &gt;</span>
            </Button>
            <Button
              type="button"
              variant="link"
              className="text-actionable underline text-sm"
              onClick={onSaveDraft}
            >
              <span>Save Draft and Finish Later</span>
            </Button>
          </div>
        ) : (
          <div className="flex-col flex mt-8 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="ml-auto"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>{editMode ? 'Update Profile' : 'Save Changes'}</span>
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
